// @ts-nocheck
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/layout/PageHeader";
import { StatCard } from "../components/StatCard";
import { useLang } from "../lib/lang";

const API = import.meta.env.VITE_API_URL ?? "";

interface AppIntegration {
  id: number;
  name: string;
  type: string;
  apiKey: string;
  active: boolean;
  requests: number;
  createdAt: string;
}

async function fetchApps(): Promise<AppIntegration[]> {
  const r = await fetch(`${API}/api/apps`);
  return r.json();
}

export function MyApps() {
  const { t } = useLang();
  const d = t.myApps;
  const qc = useQueryClient();
  const { data: apps = [], isLoading } = useQuery({ queryKey: ["apps"], queryFn: fetchApps });

  const [showForm, setShowForm] = useState(false);
  const [appName, setAppName] = useState("");
  const [appType, setAppType] = useState("REST API");
  const [visibleKeys, setVisibleKeys] = useState<Set<number>>(new Set());

  const create = useMutation({
    mutationFn: async (body: object) => {
      const r = await fetch(`${API}/api/apps`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps"] }),
  });

  const del = useMutation({
    mutationFn: async (id: number) => {
      await fetch(`${API}/api/apps/${id}`, { method: "DELETE" });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps"] }),
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await create.mutateAsync({ name: appName, type: appType });
    setAppName(""); setAppType("REST API");
    setShowForm(false);
  };

  const toggleKeyVisibility = (id: number) => {
    const next = new Set(visibleKeys);
    if (next.has(id)) next.delete(id); else next.add(id);
    setVisibleKeys(next);
  };

  const maskKey = (key: string) => key.slice(0, 12) + "••••••••••••" + key.slice(-4);

  const totalApps = apps.length;
  const activeApps = apps.filter(a => a.active).length;
  const totalRequests = apps.reduce((sum, a) => sum + a.requests, 0);

  return (
    <div>
      <PageHeader
        title={d.title}
        actions={[{ label: d.newApp, icon: "fas fa-plus", onClick: () => setShowForm(true) }]}
      />

      <div className="px-6 pb-6 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <StatCard label={d.totalApps} value={totalApps} iconClass="fas fa-cube" gradient="primary" />
          <StatCard label={d.activeApps} value={activeApps} iconClass="fas fa-check-circle" gradient="success" />
          <StatCard label={d.totalRequests} value={totalRequests.toLocaleString()} iconClass="fas fa-exchange-alt" gradient="info" />
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9ecef]">
                <h5 className="font-semibold text-[#32325d]">{d.newApp}</h5>
                <button onClick={() => setShowForm(false)} className="text-[#8898aa] hover:text-[#32325d]"><i className="fas fa-times" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.appName}</label>
                  <input value={appName} onChange={e => setAppName(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder={d.appNamePlaceholder} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.appType}</label>
                  <select value={appType} onChange={e => setAppType(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]">
                    <option>REST API</option>
                    <option>Webhook</option>
                    <option>Zapier</option>
                    <option>Make (Integromat)</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-[#e9ecef] rounded-lg text-[#525f7f]">{t.common.cancel}</button>
                  <button type="submit" disabled={create.isPending} className="px-4 py-2 text-sm gradient-primary text-white rounded-lg shadow-sm disabled:opacity-50">
                    {create.isPending ? t.common.creating : d.newApp}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-12 text-center">
            <i className="fas fa-spinner fa-spin text-2xl text-[#1171ef]" />
          </div>
        ) : apps.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-12 text-center">
            <i className="fas fa-cube text-4xl text-[#cad1d7] mb-3 block" />
            <p className="text-[#8898aa] text-sm mb-1">{d.noApps}</p>
            <p className="text-[#adb5bd] text-xs mb-4">{d.noAppsHint}</p>
            <button onClick={() => setShowForm(true)} className="gradient-primary text-white px-5 py-2 rounded-lg text-sm shadow-sm">
              <i className="fas fa-plus me-2" />{d.newApp}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {apps.map(app => (
              <div key={app.id} className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="icon-shape gradient-primary w-10 h-10 text-base">
                      <i className="fas fa-cube" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-[#32325d]">{app.name}</h5>
                      <p className="text-xs text-[#8898aa] mt-0.5">{app.type} · Created {new Date(app.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${app.active ? "bg-[#d4edda] text-[#155724]" : "bg-[#e9ecef] text-[#525f7f]"}`}>
                      {app.active ? d.active : d.inactive}
                    </span>
                    <button onClick={() => { if (confirm(d.deleteConfirm)) del.mutate(app.id); }} className="text-[#cad1d7] hover:text-[#f5365c] text-sm">
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                </div>

                <div className="bg-[#f8f9fe] rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-[#8898aa] font-semibold uppercase tracking-wider mb-1">{d.apiKey}</p>
                    <code className="text-xs font-mono text-[#32325d]">
                      {visibleKeys.has(app.id) ? app.apiKey : maskKey(app.apiKey)}
                    </code>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => toggleKeyVisibility(app.id)} className="text-xs text-[#8898aa] hover:text-[#1171ef] px-2 py-1 rounded hover:bg-white">
                      <i className={`fas fa-${visibleKeys.has(app.id) ? "eye-slash" : "eye"}`} />
                    </button>
                    <button onClick={() => navigator.clipboard.writeText(app.apiKey)} className="text-xs text-[#8898aa] hover:text-[#1171ef] px-2 py-1 rounded hover:bg-white">
                      <i className="fas fa-copy" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-3 text-xs text-[#8898aa]">
                  <span><i className="fas fa-exchange-alt me-1" />{app.requests.toLocaleString()} {d.requests}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
